import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';

interface ChecklistModalProps {
  open: boolean;
  handleClose: () => void;
}

const ChecklistModal: React.FC<ChecklistModalProps> = ({
  open,
  handleClose,
}) => {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: 'ATAB_Membership_Checklist',
    pageStyle: `
      @page { margin: 0; }
      body { margin: 0; display: flex; justify-content: center; align-items: center; }
      img { max-width: 100%; max-height: 100vh; object-fit: contain; }
    `,
  });

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = '/checklist.jpg';
    link.download = 'ATAB_Membership_Checklist.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: '#1113a2',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant='h6'
          component='div'
          sx={{ fontWeight: 600, fontSize: '1rem' }}
        >
          Checklist for ATAB Membership
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title='Print Checklist'>
            <IconButton
              aria-label='print'
              onClick={handlePrint}
              sx={{
                color: '#ffffff',
                p: 0.5,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' },
              }}
            >
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='Download Checklist'>
            <IconButton
              aria-label='download'
              onClick={handleDownload}
              sx={{
                color: '#ffffff',
                p: 0.5,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' },
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <IconButton
            aria-label='close'
            onClick={handleClose}
            sx={{
              color: '#ffffff',
              p: 0.5,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 1, bgcolor: '#f8f9fa', textAlign: 'center' }}>
        <Box ref={printRef}>
          <Box
            component='img'
            src='/checklist.jpg'
            alt='Checklist for ATAB Membership'
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: '80vh',
              objectFit: 'contain',
              borderRadius: 1,
              display: 'block',
              margin: '0 auto',
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChecklistModal;
